import React, { useState } from "react";
import { Link } from "react-router";
import GridShape from "../../components/common/GridShape";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import Checkbox from "../../components/form/input/Checkbox";
import PageMeta from "../../components/common/PageMeta";
import RegisterLoader from "../../components/common/RegisterLoader";
import { registerUser } from "../../services/authService";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFallback, localizeApiMessage } from "../../utils/apiMessageI18n";
import { ChevronLeftIcon } from "../../icons";

export default function SignUp() {
  const { language } = useLanguage();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const canSubmit =
    isChecked &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    !isSubmitting;
  const signInLinkText = getLocalizedFallback(language, {
    pt: "Ir para entrar",
    es: "Ir a iniciar sesion",
    en: "Go to sign in",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await registerUser({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        lgpd_consent: isChecked,
      });

      setSuccessMessage(
        getLocalizedFallback(language, {
          pt: "Cadastro realizado com sucesso! Enviamos um e-mail para você confirmar sua conta.",
          es: "Registro realizado con exito. Enviamos un correo para confirmar tu cuenta.",
          en: "Registration successful! We sent you an email to confirm your account.",
        })
      );
    } catch (error) {
      const fallback = getLocalizedFallback(language, {
        pt: "Nao foi possivel realizar o cadastro. Tente novamente.",
        es: "No se pudo completar el registro. Intenta nuevamente.",
        en: "Could not complete registration. Please try again.",
      });
      const rawMessage = error instanceof Error ? error.message : "";
      const localizedMessage = localizeApiMessage(rawMessage, language) || fallback;
      setErrorMessage(localizedMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageMeta
        title="SignUp Dashboard | TailAdmin - Next.js Admin Dashboard Template"
        description="This is SignUp Tables Dashboard page for TailAdmin - Tailwind CSS Admin Dashboard Template"
      />
      {isSubmitting && <RegisterLoader />}
      <div className="relative flex w-full h-screen overflow-hidden bg-white z-1 dark:bg-gray-900">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            {!successMessage && (
              <div className="mb-5 sm:mb-8">
                <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                  Sign Up
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter your email to sign up!
                </p>
              </div>
            )}
            <div>
              {successMessage ? (
                <div className="space-y-5">
                  <p className="text-sm text-success-600" role="status">
                    {successMessage}
                  </p>
                  <Link
                    to="/signin"
                    className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <ChevronLeftIcon />
                    <span>{signInLinkText}</span>
                  </Link>
                </div>
              ) : (
                <>
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        {/* <!-- First Name --> */}
                        <div className="sm:col-span-1">
                          <Label>
                            First Name<span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="fname"
                            name="fname"
                            placeholder="Enter your first name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                          />
                        </div>
                        {/* <!-- Last Name --> */}
                        <div className="sm:col-span-1">
                          <Label>
                            Last Name<span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="lname"
                            name="lname"
                            placeholder="Enter your last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* <!-- Email --> */}
                      <div>
                        <Label>
                          Email<span className="text-error-500">*</span>
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      {/* <!-- Checkbox --> */}
                      <div className="flex items-center gap-3">
                        <Checkbox
                          className="w-5 h-5"
                          checked={isChecked}
                          onChange={setIsChecked}
                        />
                        <p className="inline-block font-normal text-gray-500 dark:text-gray-400">
                          By creating an account means you agree to the{" "}
                          <span className="text-gray-800 dark:text-white/90">
                            Terms and Conditions,
                          </span>{" "}
                          and our{" "}
                          <span className="text-gray-800 dark:text-white">
                            Privacy Policy
                          </span>
                        </p>
                      </div>
                      {/* <!-- Button --> */}
                      <div>
                        <button
                          type="submit"
                          disabled={!canSubmit}
                          className={`flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition shadow-theme-xs ${
                            canSubmit
                              ? "bg-brand-500 hover:bg-brand-600"
                              : "cursor-not-allowed bg-gray-400"
                          }`}
                        >
                          {isSubmitting ? "Submitting..." : "Sign Up"}
                        </button>
                      </div>
                      {errorMessage && (
                        <p className="text-sm text-error-500" role="alert">
                          {errorMessage}
                        </p>
                      )}
                    </div>
                  </form>
                  <div className="mt-5">
                    <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                      Already have an account?
                      <Link
                        to="/signin"
                        className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      >
                        Sign In
                      </Link>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          {/* <!-- ===== Common Grid Shape Start ===== --> */}
          <GridShape />
          {/* <!-- ===== Common Grid Shape End ===== --> */}
          <div className="flex flex-col items-center max-w-lg">
            <div className="block mb-4">
              <img
                src="./images/logo/auth-logo.svg"
                alt="Logo"
                className="w-[520px] max-w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
